import React from 'react';
import './FinmapPage.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import { Bar, Doughnut, Pie } from 'react-chartjs-2';
import MermaidDiagram from '../components/MermaidDiagram';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const FinmapPage = () => {
    // Chart Data Configuration

    // 1. Budgeting Behavior
    const budgetingBehaviorData = {
        labels: ['Budget Regularly', 'Budget Sometimes', 'Don\'t Budget'],
        datasets: [{
            label: 'Percentage of Respondents',
            data: [42.7, 27.9, 29.4],
            backgroundColor: ['#27ae60', '#f39c12', '#e74c3c'],
            borderWidth: 1,
            borderColor: ['#229954', '#d68910', '#cb4335']
        }]
    };

    const budgetingBehaviorOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 50,
                title: { display: true, text: 'Percentage (%)' }
            }
        }
    };

    // 2. Budgeting Methods
    const budgetingMethodsData = {
        labels: ['Mobile Apps', 'Excel/Sheets', 'Physical Register', 'Mental Tracking', 'No Method'],
        datasets: [{
            data: [31.5, 28.0, 23.1, 10.5, 6.9],
            backgroundColor: ['#3498db', '#9b59b6', '#e67e22', '#f39c12', '#95a5a6'],
            borderWidth: 2,
            borderColor: '#ffffff'
        }]
    };

    const budgetingMethodsOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'right' }
        }
    };

    // 3. Banking Problems
    const bankingProblemsData = {
        labels: [
            'Confusion During Use',
            'Avoid Opening App',
            'Security Concerns',
            'Performance Issues',
            'Complex Registration',
            'No Bill Reminders',
            'Missing Features'
        ],
        datasets: [{
            label: 'Users Affected (%)',
            data: [33, 25, 21, 19, 18, 22, 38],
            backgroundColor: '#e74c3c',
            borderColor: '#c0392b',
            borderWidth: 1
        }]
    };

    const bankingProblemsOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false }
        },
        scales: {
            x: {
                beginAtZero: true,
                max: 40,
                title: { display: true, text: 'Percentage of Users Experiencing Issue (%)' }
            }
        }
    };

    // 4. Market Readiness
    const marketReadinessData = {
        labels: ['Ready to Switch (50.3%)', 'Considering Switch (28.7%)', 'Not Interested (21.0%)'],
        datasets: [{
            data: [50.3, 28.7, 21.0],
            backgroundColor: ['#27ae60', '#f39c12', '#95a5a6'],
            borderWidth: 2,
            borderColor: '#ffffff'
        }]
    };

    const marketReadinessOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' }
        }
    };

    return (
        <div className="finmap-wrapper">
            <div className="finmap-container">
                <div className="finmap-header">
                    <h1>Finmap</h1>
                    <h2>Market Research Analysis Report</h2>
                    <h3 style={{ fontWeight: 'normal', color: '#6c757d', fontSize: '1.2rem', marginTop: '0' }}>Financial Management & Banking Application Study</h3>
                    <div className="finmap-report-meta">
                        <p><strong>Proposed Solution:</strong> An Integrated Financial Management Ecosystem</p>
                        <p><strong>Study Period:</strong> July 2025</p>
                        <p><strong>Sample Size:</strong> 246 Respondents</p>
                    </div>
                </div>

                <div className="finmap-video-section">
                    <h2>Platform Overview</h2>
                    <p style={{ marginBottom: '20px', color: '#6c757d' }}>Watch the concept video below to understand our vision.</p>
                    <div className="finmap-video-container">
                        <iframe
                            width="560"
                            height="315"
                            src="https://www.youtube.com/embed/trFGku3khp0"
                            title="Finmap Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                </div>

                <div className="finmap-executive-summary">
                    <h2>Executive Summary</h2>
                    <p>This comprehensive market research study examines user behavior, pain points, and opportunities in the financial management and digital banking sector. Our analysis of 246 respondents reveals significant gaps in financial literacy and substantial user dissatisfaction with current solutions, presenting a compelling market opportunity for an integrated financial management platform.</p>

                    <p style={{ marginTop: '15px' }}><strong>Key Finding:</strong> 57.3% of users do not maintain regular budgeting habits, while 49% experience significant problems with their current financial management methods. Combined with a 50.3% willingness to switch platforms, this indicates a substantial addressable market for innovative solutions.</p>
                </div>

                <div className="finmap-key-metrics">
                    <div className="finmap-metric-card">
                        <div className="finmap-metric-value">57.3%</div>
                        <div className="finmap-metric-label">Poor Financial Discipline</div>
                        <div className="finmap-metric-description">Respondents who do not budget regularly or only budget occasionally</div>
                    </div>
                    <div className="finmap-metric-card">
                        <div className="finmap-metric-value">49.0%</div>
                        <div className="finmap-metric-label">Current Solution Problems</div>
                        <div className="finmap-metric-description">Users experiencing difficulties with existing budgeting methods</div>
                    </div>
                    <div className="finmap-metric-card">
                        <div className="finmap-metric-value">50.3%</div>
                        <div className="finmap-metric-label">Switch Intent</div>
                        <div className="finmap-metric-description">Ready to adopt integrated banking and budgeting solution</div>
                    </div>
                    <div className="finmap-metric-card">
                        <div className="finmap-metric-value">79.0%</div>
                        <div className="finmap-metric-label">Market Opportunity</div>
                        <div className="finmap-metric-description">Combined users willing or potentially willing to switch (Yes + Maybe)</div>
                    </div>
                </div>

                <div className="finmap-chart-section">
                    <h2 className="finmap-chart-title">Figure 1: Budgeting Behavior Analysis</h2>
                    <div className="finmap-chart-container">
                        <Bar data={budgetingBehaviorData} options={budgetingBehaviorOptions} />
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#6c757d', fontStyle: 'italic' }}>Source: Financial Management Survey (n=143)</p>
                </div>

                <div className="finmap-chart-section">
                    <h2 className="finmap-chart-title">Figure 2: Current Budgeting Methods Distribution</h2>
                    <div className="finmap-chart-container">
                        <Doughnut data={budgetingMethodsData} options={budgetingMethodsOptions} />
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#6c757d', fontStyle: 'italic' }}>Source: Financial Management Survey (n=143)</p>
                </div>

                <div className="finmap-chart-section">
                    <h2 className="finmap-chart-title">Figure 3: User Experience Issues - Banking Applications</h2>
                    <div className="finmap-chart-container">
                        <Bar data={bankingProblemsData} options={bankingProblemsOptions} />
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#6c757d', fontStyle: 'italic' }}>Source: Banking Experience Survey (n=103)</p>
                </div>

                <div className="finmap-chart-section">
                    <h2 className="finmap-chart-title">Figure 4: Market Readiness - Platform Switch Intent</h2>
                    <div className="finmap-chart-container">
                        <Pie data={marketReadinessData} options={marketReadinessOptions} />
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#6c757d', fontStyle: 'italic' }}>Source: Combined Survey Data (n=246)</p>
                </div>

                <div className="finmap-data-table-section">
                    <h2>Table 1: Problem Category Analysis</h2>
                    <table className="finmap-table">
                        <thead>
                            <tr>
                                <th>Problem Category</th>
                                <th>Frequency</th>
                                <th>Impact Level</th>
                                <th>User Quotes</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Consistency & Discipline</strong></td>
                                <td>31%</td>
                                <td><span className="finmap-badge finmap-badge-critical">Critical</span></td>
                                <td>"It requires consistency, and the cycle breaks every now and then"</td>
                            </tr>
                            <tr>
                                <td><strong>Manual Entry Burden</strong></td>
                                <td>28%</td>
                                <td><span className="finmap-badge finmap-badge-critical">Critical</span></td>
                                <td>"Forget to write...becomes messy to understand"</td>
                            </tr>
                            <tr>
                                <td><strong>Lack of Integration</strong></td>
                                <td>24%</td>
                                <td><span className="finmap-badge finmap-badge-warning">High</span></td>
                                <td>"No integration between banking and budgeting"</td>
                            </tr>
                            <tr>
                                <td><strong>Complex Navigation</strong></td>
                                <td>33%</td>
                                <td><span className="finmap-badge finmap-badge-warning">High</span></td>
                                <td>"New interface is annoying especially scrolling to select account"</td>
                            </tr>
                            <tr>
                                <td><strong>Performance Issues</strong></td>
                                <td>19%</td>
                                <td><span className="finmap-badge finmap-badge-warning">High</span></td>
                                <td>"It is slow", "Slow in showing transactions"</td>
                            </tr>
                            <tr>
                                <td><strong>Security Concerns</strong></td>
                                <td>21%</td>
                                <td><span className="finmap-badge finmap-badge-warning">High</span></td>
                                <td>"Not confident about security"</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="finmap-insights-section">
                    <h2>Critical User Pain Points</h2>

                    <h3 className="finmap-h3">1. Financial Management Challenges</h3>
                    <ul className="finmap-finding-list">
                        <li className="finmap-finding-item finmap-critical-finding">
                            <strong>Lack of Financial Discipline</strong>
                            57.3% of respondents do not maintain regular budgeting practices, indicating severe gaps in financial literacy and habit formation.
                        </li>
                        <li className="finmap-finding-item finmap-critical-finding">
                            <strong>Manual Process Inefficiency</strong>
                            Users report forgetting to record transactions, losing physical registers, and finding manual tracking tedious and error-prone.
                        </li>
                        <li className="finmap-finding-item finmap-critical-finding">
                            <strong>Fragmented Experience</strong>
                            Current solutions require users to manage multiple apps for banking and budgeting, creating friction and reducing engagement.
                        </li>
                    </ul>

                    <h3 className="finmap-h3">2. Banking Application Issues</h3>
                    <ul className="finmap-finding-list">
                        <li className="finmap-finding-item">
                            <strong>User Interface Complexity</strong>
                            33% of users report confusion while navigating banking applications, indicating poor UX design.
                        </li>
                        <li className="finmap-finding-item">
                            <strong>Performance Problems</strong>
                            Slow transaction processing, delayed balance updates, and poor network handling frustrate users.
                        </li>
                        <li className="finmap-finding-item">
                            <strong>Missing Features</strong>
                            Users request bill payment reminders, spending analytics, investment options, and budgeting tools.
                        </li>
                    </ul>

                    <h3 className="finmap-h3">3. Market Opportunities</h3>
                    <ul className="finmap-finding-list">
                        <li className="finmap-finding-item finmap-opportunity-finding">
                            <strong>High Switch Intent</strong>
                            50.3% of users are willing to switch to an integrated platform, with an additional 28.7% potentially willing.
                        </li>
                        <li className="finmap-finding-item finmap-opportunity-finding">
                            <strong>Feature Demand</strong>
                            Strong demand for automated categorization, bill reminders, investment options, and financial insights.
                        </li>
                        <li className="finmap-finding-item finmap-opportunity-finding">
                            <strong>Security Enhancement</strong>
                            Opportunity to differentiate through robust security features and biometric authentication.
                        </li>
                    </ul>
                </div>

                <div className="finmap-recommendations">
                    <h2>Strategic Recommendations</h2>

                    <div className="finmap-recommendation-grid">
                        <div className="finmap-recommendation-box finmap-do-box">
                            <h4>✓ Priority Features to Implement</h4>
                            <ul className="finmap-recommendation-list">
                                <li><strong>Automated Transaction Categorization</strong> - Eliminate manual entry burden</li>
                                <li><strong>Smart Budgeting Tools</strong> - Spending buckets and analytics</li>
                                <li><strong>Bill Payment Reminders</strong> - Proactive notifications</li>
                                <li><strong>Biometric Authentication</strong> - Quick and secure access</li>
                                <li><strong>Real-time Sync</strong> - Instant balance updates</li>
                                <li><strong>Financial Insights Dashboard</strong> - Visual spending patterns</li>
                                <li><strong>Investment Integration</strong> - Stocks, mutual funds, gold</li>
                                <li><strong>In-app Customer Support</strong> - Direct help line</li>
                            </ul>
                        </div>

                        <div className="finmap-recommendation-box finmap-dont-box">
                            <h4>✗ Critical Issues to Avoid</h4>
                            <ul className="finmap-recommendation-list">
                                <li><strong>Slow Performance</strong> - Ensure fast load times</li>
                                <li><strong>Complex Registration</strong> - Simplify beneficiary adding</li>
                                <li><strong>Poor Network Handling</strong> - Optimize for low connectivity</li>
                                <li><strong>Confusing Navigation</strong> - Intuitive UI/UX design</li>
                                <li><strong>Delayed Transactions</strong> - Real-time processing</li>
                                <li><strong>Manual Data Entry</strong> - Maximize automation</li>
                                <li><strong>Fragmented Features</strong> - Integrated experience</li>
                                <li><strong>Lack of Guidance</strong> - Onboarding and tooltips</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="finmap-chart-section">
                    <h2 className="finmap-chart-title">System Architecture: Middleware Backend</h2>
                    <p style={{ marginBottom: '15px', color: '#6c757d' }}>High-level overview of how Finmap integrates with partner banks.</p>
                    <MermaidDiagram chart={`
flowchart LR
    subgraph User_Device["Finmap Mobile App (User Device)"]
        UI["Finmap UI & UX"]
        Budgets["Local Budget View<br>(Envelopes, Insights)"]
    end

    subgraph Finmap_Backend["Finmap Middleware Backend"]
        Auth["User Auth & KYC Layer"]
        Profile["User Profile / Budget Engine"]
        TXEngine["Transaction Decision Engine<br>(Budget Check, Rules Engine)"]
        Ledger["Internal Ledger & Analytics"]
        Router["Bank Routing Layer<br>(Easypaisa / JazzCash / Askari)"]
        Logs["Audit Logs & Alerts"]
    end

    subgraph Partner_Banks["Partner Microfinance / Wallet APIs"]
        EP["Easypaisa APIs"]
        JC["JazzCash APIs"]
        AK["Askari Bank APIs"]
    end

    UI -->|Login / Requests| Auth
    Auth --> Profile
    Profile --> Budgets
    UI -->|Initiate Payment| TXEngine
    TXEngine -->|Check Limit / Envelope| Profile
    TXEngine --> Ledger
    TXEngine -->|Route Based on User Choice| Router

    Router --> EP
    Router --> JC
    Router --> AK

    Partner_Banks -->|Success/Failure| Router
    Router --> TXEngine
    TXEngine --> UI
    Ledger --> Budgets
`} />
                </div>

                <div className="finmap-chart-section">
                    <h2 className="finmap-chart-title">User Flow: Smart Budget Engine</h2>
                    <p style={{ marginBottom: '15px', color: '#6c757d' }}>How the smart budget engine processes transactions in real-time.</p>
                    <MermaidDiagram chart={`
flowchart LR
    A["<b>👤 User</b><br>Finmap App"]
    B["<b>📱 Select Wallet</b><br>Easypaisa / JazzCash / Askari"]
    C["<b>💳 Finmap Smart Budget Engine</b><br>(Checks Limits)"]
    D["<b>🚫 Blocked</b><br>Budget Exceeded"]
    E["<b>✅ Approved</b><br>Send to Bank"]
    F["<b>🏦 Bank / Wallet</b><br>API Transaction"]
    G["<b>🔁 Bank Reply</b><br>Success / Failure"]
    H["<b>📊 Spending Updated</b><br>Envelopes & Insights"]

    A --> B --> C
    C -->|Budget Exceeded| D
    C -->|Budget OK| E
    E --> F --> G --> H --> A
`} />
                </div>

                <div className="finmap-conclusion">
                    <h2>Conclusion & Market Opportunity Assessment</h2>
                    <p>
                        Our comprehensive analysis of 246 respondents reveals a <strong>significant market opportunity</strong> in the financial management sector. The confluence of poor financial literacy (57.3% irregular budgeting), widespread dissatisfaction with current solutions (49% facing problems), and high willingness to switch platforms (50.3% ready, 79% total potential) creates optimal conditions for market entry.
                    </p>
                    <p style={{ marginTop: '20px' }}>
                        The data strongly supports the development of an <strong>integrated financial management platform</strong> that addresses the fragmentation between banking and budgeting applications. Users are actively seeking a solution that combines automated transaction tracking, intelligent categorization, financial insights, and seamless banking operations in a single, performant application.
                    </p>
                    <div style={{ marginTop: '20px', fontSize: '1.2rem', textAlign: 'center', borderTop: '2px solid rgba(255,255,255,0.2)', paddingTop: '20px' }}>
                        <p><strong>Total Addressable Market: 79% of current banking app users</strong></p>
                        <p><strong>Immediate Target Market: 50.3% ready to switch</strong></p>
                        <p><strong>Revenue Opportunity: High (Premium features, subscription model viable)</strong></p>
                    </div>

                    <div className="finmap-cta">
                        <a href="mailto:finmap.pk@gmail.com" className="finmap-button">Contact for Investment</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinmapPage;
